// First-party analytics beacon → FRACTO collector (cross-origin to
// ericstevenson.com). Owned demand telemetry, independent of GA4. Uses
// navigator.sendBeacon with a text/plain body (CORS-simple → no preflight).
// Privacy-light (anon first-party uuid, no PII), fail-silent, and skips
// automated/prerender renders at the source.

const COLLECT_URL = "https://ericstevenson.com/api/fracto/collect";
const SITE = "votomoto";
const VID_KEY = "_es_vid";
const SID_KEY = "_es_sid";
const SID_TS_KEY = "_es_sid_ts";
const SESSION_IDLE_MS = 30 * 60 * 1000;

function isAutomated() {
  try {
    if (navigator.webdriver) return true;
    if (!navigator.sendBeacon && typeof fetch === "undefined") return true; // non-browser
    const ua = navigator.userAgent || "";
    // jsdom = the test environment; never beacon from there.
    return /Headless|jsdom|node\.js|bot|crawl|spider|slurp|preview|prerender|phantom|monitor/i.test(ua);
  } catch (_) { return true; }
}

function uuid() {
  try {
    if (window.crypto && window.crypto.randomUUID) return window.crypto.randomUUID();
  } catch (_) {}
  return "x-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function visitorId() {
  try {
    let v = localStorage.getItem(VID_KEY);
    if (!v) { v = uuid(); localStorage.setItem(VID_KEY, v); }
    return v;
  } catch (_) { return null; }
}

function sessionId() {
  try {
    const now = Date.now();
    const ts = parseInt(sessionStorage.getItem(SID_TS_KEY) || "0", 10);
    let s = sessionStorage.getItem(SID_KEY);
    if (!s || !ts || now - ts > SESSION_IDLE_MS) { s = uuid(); sessionStorage.setItem(SID_KEY, s); }
    sessionStorage.setItem(SID_TS_KEY, String(now));
    return s;
  } catch (_) { return null; }
}

const ATTR_KEY = "_es_attr";
const ATTR_PARAMS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "variant", "exp"];

// First-touch attribution for the session: capture utm_* (campaign/marketing
// measurement — e.g. the Shareable engine's per-post utm_content) and
// variant/exp (A/B testing) from the landing URL once, persist them, and
// attach to every event. First-touch wins (the campaign that brought the
// visitor), so later in-session navigations don't overwrite it. Same substrate
// as the other portfolio properties — the FRACTO Analytics tab slices on it.
function attribution() {
  try {
    const stored = sessionStorage.getItem(ATTR_KEY);
    const prev = stored ? JSON.parse(stored) : null;
    // First-touch wins once we actually have attribution.
    if (prev && Object.keys(prev).length > 0) return prev;

    // No (or empty) attribution yet — read the current URL. This upgrades an
    // organic landing ({}) to a campaign/variant if a later same-session URL
    // carries one (e.g. direct → then a utm-tagged link in the same tab).
    const qp = new URLSearchParams(window.location.search);
    const attr = {};
    for (const k of ATTR_PARAMS) {
      const v = qp.get(k);
      if (v) attr[k] = v.slice(0, 120); // bound length; skip absent params
    }
    if (Object.keys(attr).length > 0) {
      sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
      return attr;
    }
    // Still nothing — persist an empty marker so organic events are cheap, but
    // leave the door open to upgrade on a later attributed URL.
    if (!prev) sessionStorage.setItem(ATTR_KEY, JSON.stringify({}));
    return {};
  } catch (_) {
    return {};
  }
}

export function track(event, props) {
  try {
    if (isAutomated()) return;
    const payload = {
      site: SITE,
      event: event || "pageview",
      path: window.location.pathname,
      referrer: document.referrer || null,
      vid: visitorId(),
      sid: sessionId(),
      props: { host: window.location.host, ...attribution(), ...(props || {}) },
    };
    const body = JSON.stringify(payload);
    if (navigator.sendBeacon) {
      navigator.sendBeacon(COLLECT_URL, new Blob([body], { type: "text/plain" }));
    } else {
      fetch(COLLECT_URL, { method: "POST", body, keepalive: true, mode: "no-cors" }).catch(() => {});
    }
  } catch (_) {}
}

export function trackPageview() {
  track("pageview");
}

// --- Voto refresh beacons (spec v1 §3) -------------------------------------
// Client-side mirrors of the backend's authoritative events, sent to the same
// first-party collector as pageviews. Names match the spec so the FRACTO
// Analytics tab can line the two up; `client: 'web'` marks the mirror.

export function trackShare(channel, props) {
  track("share", { client: "web", channel, ...(props || {}) });
}

export function trackVote(source, props) {
  track("vote", { client: "web", source, ...(props || {}) });
}

export function trackBoothView(props) {
  track("booth_view", { client: "web", ...(props || {}) });
}

export function trackBoothVote(credited, props) {
  track("booth_vote", { client: "web", credited: !!credited, ...(props || {}) });
  if (credited) track("credit_earned", { client: "web", reason: "vote_earned", ...(props || {}) });
}

export function trackBoostView(props) {
  track("boost_view", { client: "web", ...(props || {}) });
}

export function trackBoost(votes, props) {
  track("boost", { client: "web", votes, ...(props || {}) });
}

export function trackSharePageView(props) {
  track("share_page_view", { client: "web", ...(props || {}) });
}

export function trackCreditsView(props) {
  track("credits_view", { client: "web", ...(props || {}) });
}
